import { useContext, useEffect, useState } from "react";
import CurrentUserContext from "../../contexts/CurrentUserContext";
import { getCustomerInvoices } from "../../lib/api";
import { FULL_ACCESS } from "../../utils/licenseAccess";

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(parsedDate);
};

const isLicenseExpiringSoon = (licenseExpiresAt) => {
  if (!licenseExpiresAt) {
    return false;
  }

  const expiresAt = new Date(licenseExpiresAt);

  if (Number.isNaN(expiresAt.getTime())) {
    return false;
  }

  const endOfExpirationDay = Date.UTC(
    expiresAt.getUTCFullYear(),
    expiresAt.getUTCMonth(),
    expiresAt.getUTCDate(),
    23,
    59,
    59,
    999
  );

  const msUntilExpiration = endOfExpirationDay - Date.now();

  return msUntilExpiration >= 0 && msUntilExpiration <= FIVE_DAYS_MS;
};

const isExpiringToday = (licenseExpiresAt) => {
  if (!licenseExpiresAt) {
    return false;
  }

  const expiresAt = new Date(licenseExpiresAt);

  if (Number.isNaN(expiresAt.getTime())) {
    return false;
  }

  // Compare calendar dates in UTC so this matches the UTC-formatted date shown
  // to the user (licenseExpiresAt is a midnight-UTC calendar date).
  const now = new Date();

  return (
    expiresAt.getUTCFullYear() === now.getUTCFullYear() &&
    expiresAt.getUTCMonth() === now.getUTCMonth() &&
    expiresAt.getUTCDate() === now.getUTCDate()
  );
};

const AccountStatusBanner = ({ children, hidden = false }) => {
  const { currentUser } = useContext(CurrentUserContext);

  const isCustomerFullAccess =
    !hidden &&
    currentUser?.userType === "Customer" &&
    currentUser?.userAccessType === FULL_ACCESS;

  const licenseExpiresAt = currentUser?.customer?.licenseExpiresAt;
  const isOnTrial = currentUser?.customer?.status === "ON_TRIAL";

  const [overdueInvoice, setOverdueInvoice] = useState(null);

  useEffect(() => {
    if (!isCustomerFullAccess) {
      setOverdueInvoice(null);
      return;
    }

    let cancelled = false;

    const fetchOverdueInvoice = async () => {
      try {
        const response = await getCustomerInvoices({
          pageNumber: 1,
          pageSize: 1,
          isOverdue: true,
        });
        const invoices = response?.data?.data?.invoices || [];
        if (!cancelled) {
          setOverdueInvoice(invoices[0] || null);
        }
      } catch (error) {
        if (!cancelled) {
          setOverdueInvoice(null);
        }
      }
    };

    fetchOverdueInvoice();

    return () => {
      cancelled = true;
    };
  }, [isCustomerFullAccess]);

  let message = null;

  if (isCustomerFullAccess) {
    if (overdueInvoice) {
      message = `Invoice ${overdueInvoice.invoiceDocumentNumber} is overdue. It was due on ${formatDate(
        overdueInvoice.dueDate
      )}.`;
    } else if (isLicenseExpiringSoon(licenseExpiresAt)) {
      const when = isExpiringToday(licenseExpiresAt)
        ? "today"
        : `on ${formatDate(licenseExpiresAt)}`;
      message = isOnTrial
        ? `Your Nexus trial will end ${when}.`
        : `Your Nexus license will expire ${when}.`;
    }
  }

  return (
    <>
      {message && (
        <div
          role="alert"
          data-testid="account-status-banner"
          className="flex items-center justify-center gap-3 px-4 py-3 bg-[#f7eee6] border-b border-[#ffb580] font-medium text-black"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 20 20"
            fill="none"
            className="shrink-0"
            aria-hidden="true"
          >
            <path
              d="M4.42651 8.96791C6.85847 4.65597 8.07446 2.5 10.0001 2.5C11.9257 2.5 13.1417 4.65597 15.5736 8.96791L15.8767 9.50522C17.8976 13.0884 18.9081 14.88 17.9949 16.19C17.0816 17.5 14.8221 17.5 10.3031 17.5H9.69703C5.17805 17.5 2.91856 17.5 2.0053 16.19C1.09203 14.88 2.10251 13.0884 4.12346 9.50523L4.42651 8.96791Z"
              stroke="#FFA526"
              strokeWidth="1.5"
            />
            <path
              d="M10 6.66602V10.8327"
              stroke="#FFA526"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <circle
              cx="10.0001"
              cy="13.3333"
              r="0.5"
              fill="#FFA526"
              stroke="#FFA526"
              strokeWidth="0.666667"
            />
          </svg>
          {message}
        </div>
      )}
      {children}
    </>
  );
};

export default AccountStatusBanner;
